import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const POS_KEY = (id) => `sk_pos_${id}`;
const LAST_KEY = "sk_last";

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => parseFloat(localStorage.getItem("sk_vol") || "1"));
  const [autoAdvance, setAutoAdvance] = useState(() => localStorage.getItem("sk_auto") === "true");
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sleepMode, setSleepMode] = useState(null); // null | minutes(number) | 'end'
  const [sleepRemaining, setSleepRemaining] = useState(0); // seconds
  const sleepEndTrackRef = useRef(false);
  const retryRef = useRef(0);

  const current = index >= 0 && index < queue.length ? queue[index] : null;

  // attach a persistent audio element
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "metadata";
    audioRef.current = audio;
    return () => { audio.pause(); audio.src = ""; };
  }, []);

  const attemptPlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const p = audio.play();
    if (p && p.catch) {
      p.then(() => { setNeedsInteraction(false); setIsPlaying(true); })
       .catch(() => { setNeedsInteraction(true); setIsPlaying(false); });
    }
  }, []);

  const loadAndPlay = useCallback((song, resumePos = 0, autoPlay = true) => {
    const audio = audioRef.current;
    if (!audio || !song) return;
    retryRef.current = 0;
    audio.src = song.stream_url;
    audio.load();
    audio.volume = volume;
    const onMeta = () => {
      const saved = resumePos || parseFloat(localStorage.getItem(POS_KEY(song.id)) || "0");
      if (saved && saved < (audio.duration || Infinity) - 2) {
        try { audio.currentTime = saved; } catch (e) {}
      }
      setDuration(audio.duration || song.duration_sec || 0);
      audio.removeEventListener("loadedmetadata", onMeta);
    };
    audio.addEventListener("loadedmetadata", onMeta);
    localStorage.setItem(LAST_KEY, song.id);
    if (autoPlay) {
      attemptPlay();
      axios.post(`${API}/songs/${song.id}/play`).catch(() => {});
    }
  }, [volume, attemptPlay]);

  const playIndex = useCallback((i, list) => {
    const q = list || queue;
    if (i < 0 || i >= q.length) return;
    setQueue(q);
    setIndex(i);
    loadAndPlay(q[i], 0, true);
  }, [queue, loadAndPlay]);

  const next = useCallback(() => {
    setIndex((i) => {
      const ni = i + 1;
      if (ni < queue.length) { loadAndPlay(queue[ni], 0, true); return ni; }
      setIsPlaying(false); return i;
    });
  }, [queue, loadAndPlay]);

  const prev = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) { audio.currentTime = 0; return; }
    setIndex((i) => {
      const pi = i - 1;
      if (pi >= 0) { loadAndPlay(queue[pi], 0, true); return pi; }
      return i;
    });
  }, [queue, loadAndPlay]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) { attemptPlay(); } else { audio.pause(); setIsPlaying(false); }
  }, [attemptPlay]);

  const seek = useCallback((sec) => {
    const audio = audioRef.current;
    if (audio && isFinite(sec)) audio.currentTime = sec;
  }, []);

  const changeVolume = useCallback((v) => {
    setVolume(v);
    localStorage.setItem("sk_vol", String(v));
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const toggleAuto = useCallback(() => {
    setAutoAdvance((a) => { localStorage.setItem("sk_auto", String(!a)); return !a; });
  }, []);

  const setSleepTimer = useCallback((mode) => {
    if (mode === "end") { sleepEndTrackRef.current = true; setSleepMode("end"); setSleepRemaining(0); }
    else if (typeof mode === "number" && mode > 0) { sleepEndTrackRef.current = false; setSleepMode(mode); setSleepRemaining(mode * 60); }
    else { sleepEndTrackRef.current = false; setSleepMode(null); setSleepRemaining(0); }
  }, []);

  // sleep timer countdown
  useEffect(() => {
    if (typeof sleepMode !== "number") return;
    if (sleepRemaining <= 0) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setSleepMode(null);
      return;
    }
    const id = setInterval(() => setSleepRemaining((r) => r - 1), 1000);
    return () => clearInterval(id);
  }, [sleepMode, sleepRemaining]);

  // audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let last = 0;
    const onTime = () => {
      setProgress(audio.currentTime);
      if (current && audio.currentTime - last > 4) {
        last = audio.currentTime;
        localStorage.setItem(POS_KEY(current.id), String(audio.currentTime));
      }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      if (current) localStorage.removeItem(POS_KEY(current.id));
      if (sleepEndTrackRef.current) {
        sleepEndTrackRef.current = false;
        setSleepMode(null);
        setIsPlaying(false);
        return;
      }
      if (autoAdvance) next(); else setIsPlaying(false);
    };
    const onError = () => {
      if (isPlaying && retryRef.current < 2 && current) {
        retryRef.current += 1;
        setTimeout(() => { audio.load(); attemptPlay(); }, 1200);
      }
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [current, autoAdvance, next, isPlaying, attemptPlay]);

  // Media Session (lockscreen + background)
  useEffect(() => {
    if (!("mediaSession" in navigator) || !current) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: current.title_si || current.title_en,
        artist: current.artist,
        album: current.album || "Swaranjaliya",
        artwork: [
          { src: current.cover_url, sizes: "512x512", type: "image/jpeg" },
          { src: current.cover_url, sizes: "256x256", type: "image/jpeg" },
        ],
      });
      navigator.mediaSession.setActionHandler("play", () => attemptPlay());
      navigator.mediaSession.setActionHandler("pause", () => { audioRef.current?.pause(); });
      navigator.mediaSession.setActionHandler("previoustrack", () => prev());
      navigator.mediaSession.setActionHandler("nexttrack", () => next());
      navigator.mediaSession.setActionHandler("seekto", (d) => { if (d.seekTime != null) seek(d.seekTime); });
    } catch (e) {}
  }, [current, attemptPlay, prev, next, seek]);

  useEffect(() => {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    }
  }, [isPlaying]);

  const value = {
    queue, setQueue, index, current, isPlaying, progress, duration, volume,
    autoAdvance, needsInteraction, expanded, setExpanded,
    sleepMode, sleepRemaining, setSleepTimer,
    playIndex, next, prev, togglePlay, seek, changeVolume, toggleAuto, attemptPlay,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}
